/* eslint-disable @typescript-eslint/no-explicit-any */
import { Duration } from 'js-joda';
import {
  find,
  forEach,
  get,
  isEmpty,
  lowerCase,
  some,
  startsWith,
  toLower,
  toUpper,
} from 'es-toolkit/compat';
import DOMPurify from 'dompurify';
import ExpireArray from 'utils/ExpireArray';
import rasterizeHTML, { RenderResult } from 'rasterizehtml';

import { NetworkPrinterJob, dashboard } from 'types';
import { constants } from '@tock/shared-app-constants';
import {
  jobFailed,
  submitJobDone,
  submitJobsDone,
  submitJobsFailed,
  versions,
} from 'actions/NetworkPrintersActions';
import { logger } from '@tock/logging';
import { safeEnumCompare } from '@tock/coretypes';

import { EpsonPrinterSeries } from 'constants/PrinterConstants';
import protos from 'utils/protos';

const DEFAULT_PRINTER_DELAY_SECONDS = 5;
const PRINT_QUEUE_ELEMENT_EXPIRATION_MILLIS = 360000; // 6 minutes

const { BusinessPrinter } = protos;

const NetworkPrinterUtils = constants([
  'CANCEL_JOBS',
  'GET_NATIVE_VERSIONS_DONE',
  'NETWORK_PRINTERS_PRINT_TEXT',
  'JOB',
  'SUBMIT_JOBS',
]);
// Temporarily created to created logs for printers at the following businesses: 20360, 20346. Will be removed once this is no logging necessary.
const verboseLoggingBusinessPrinters = new Set([
  6213, 6218, 6220, 6232, 6233, 6234, 6235, 7148, 7151, 7340, 7344, 7351, 7346, 6304,
  // testing printers for staging
  6026, 6029, 6057, 6008, 6007, 6072, 6069,
]);
// Function to determine if printer is found in set above.
function shouldLogVerbose(message: NativePrintMessage) {
  return (
    message.payload?.jobs &&
    message.payload?.jobs.filter((job) => verboseLoggingBusinessPrinters.has(job.printer.id))
      .length > 0
  );
}

interface NativePrintMessage {
  type: string;
  payload?: {
    jobs?: NetworkPrinterJob[];
    printer?: dashboard.BusinessPrinter;
    textToPrint?: string;
  };
}

interface NativePort {
  postMessage(message: NativePrintMessage): void;
}

// The in-memory queue of jobs to print
const activePrintQueue: ExpireArray<NativePrintMessage> = new ExpireArray(
  PRINT_QUEUE_ELEMENT_EXPIRATION_MILLIS,
  200,
  (expiredJob) => {
    const message = expiredJob.payload?.printer
      ? `Print queue job expired for printer: ${stringifyPrinter(expiredJob.payload?.printer)}`
      : `Print queue jobs expired: ${stringifyJobs(expiredJob.payload?.jobs)}`;
    /**
     * We expect this to occur occasionally when a printer does not acknowledge completion,
     * for example due to a network blip. We want to know about it in case jobs are expiring
     * before they are ever submitted to the printer.
     */
    logger.error(message);
    window.LOGGER?.error(message);
  }
);

function stringifyJobs(jobs?: NetworkPrinterJob[]): string {
  return (
    jobs
      ?.map((job) => {
        return `printer: ${stringifyPrinter(job.printer)},
     printPlainText: ${job.printPlainText},
     textLength: ${job.text.length} characters,
     text: ${job.text}`;
      })
      .join('\n') || 'unknown'
  );
}

function stringifyMenuCategory(menuCategories?: dashboard.IMenuCategory[]) {
  return (
    menuCategories
      ?.map((menuCategory) => {
        return `id: ${menuCategory.id},
     name: ${menuCategory.name},
     isArchived: ${menuCategory.isArchived};`;
      })
      .join('\n') || 'unknown'
  );
}

function stringifyPrinter(printer?: dashboard.BusinessPrinter) {
  return printer
    ? `
    payload.printer: {
      id: ${printer.id},
      name: ${printer.name},
      model: ${printer?.model},
      macAddress: ${printer?.macAddress},
      ipAddress: ${printer?.ipAddress},
      defaultSelected: ${printer?.defaultSelected},
      manufacturer: ${printer?.manufacturer},
      communicationType: ${printer?.communicationType},
      defaultNumCopiesToPrint: ${printer?.defaultNumCopiesToPrint},
      enableToGo: ${printer?.enableToGo},
      enableTableside: ${printer?.enableTableside},
      menuCategory: ${stringifyMenuCategory(printer?.menuCategory)},
      enablePrintForHosts: ${printer?.enablePrintForHosts},
      printVisitTags: ${printer?.printVisitTags},
      printVisitNotes: ${printer?.printVisitNotes},
      printVisitQuestions: ${printer?.printVisitQuestions},
      printGuestTags: ${printer?.printGuestTags},
      printGuestNotes: ${printer?.printGuestNotes},
      printGuestPhone: ${printer?.printGuestPhone}},
      printGuestVisitHistory: ${printer?.printGuestVisitHistory},
      printItemPrices: ${printer?.printItemPrices},
      printEmailAddresses: ${printer?.printEmailAddresses}}`
    : 'unknown';
}

function sendPendingMessage() {
  if (activePrintQueue.length === 0) {
    return;
  }
  printToNative(activePrintQueue[0] as NativePrintMessage);
}

function printToNative(message: NativePrintMessage) {
  const nativePort = getNativePort();
  if (shouldLogVerbose(message)) {
    logger.error(`payload.jobs[${stringifyJobs(message.payload?.jobs)}]}`);
  }
  if (!nativePort) {
    logger.error('Could not communicate with native interface');
    throw new Error('Could not communicate with native interface');
  }

  if (shouldLogVerbose(message)) {
    logger.error(`Sending print message to native... ${stringifyJobs(message.payload?.jobs)}`);
  }
  nativePort.postMessage(message);
  if (shouldLogVerbose(message)) {
    logger.error(`Finished sending print message to native.`);
  }
}

function printToNativeOrQueue(message: NativePrintMessage, usePrintQueue: boolean) {
  if (!usePrintQueue) {
    printToNative(message);
    return;
  }

  if (activePrintQueue.length === 0) {
    activePrintQueue.push(message);
    printToNative(message);
  } else {
    activePrintQueue.push(message);
    if (shouldLogVerbose(message)) {
      logger.error(`Enqueued job: ${stringifyJobs(message.payload?.jobs)}`);
    }
  }
}

function printFinished() {
  const message = activePrintQueue[0];
  if (message) {
    message.payload?.jobs?.shift();
    if (isEmpty(message.payload?.jobs)) {
      activePrintQueue.shift();
      sendPendingMessage();
    }
  }
}

function printFailed() {
  activePrintQueue.shift();
  sendPendingMessage();
}

function getBrowserOnNativeMessage(message: any) {
  switch (message.type) {
    case NetworkPrinterUtils.JOB_DONE:
      printFinished();
      return submitJobDone();
    case NetworkPrinterUtils.JOB_FAILED:
      printFinished();
      return jobFailed();
    case NetworkPrinterUtils.SUBMIT_JOBS_DONE:
      return submitJobsDone();
    case NetworkPrinterUtils.SUBMIT_JOBS_FAILED:
      printFailed();
      return submitJobsFailed(message.errorMessage);
    case NetworkPrinterUtils.GET_NATIVE_VERSIONS_DONE:
      return versions(message.appVersion, message.osVersion);
    default:
      return {};
  }
}

export function initializeNetworkPrinterApi(store: any) {
  if (window.onNativeMessage) {
    logger.warn('Network Printer API is already initialized. Overwriting with new store.');
  }

  window.onNativeMessage = (message: any) => {
    store.dispatch(getBrowserOnNativeMessage(message));
  };
}

function makeSubmitJobMessage(jobs: NetworkPrinterJob[]): NativePrintMessage {
  return {
    type: NetworkPrinterUtils.SUBMIT_JOBS,
    payload: {
      jobs,
    },
  };
}

function makePrintPlainTextMessage(job: NetworkPrinterJob): NativePrintMessage {
  return {
    type: NetworkPrinterUtils.NETWORK_PRINTERS_PRINT_TEXT,
    payload: {
      printer: job.printer,
      textToPrint: job.text,
    },
  };
}

export function getNativePort(): NativePort | undefined {
  return get(window, 'webkit.messageHandlers.observe') || get(window, 'tockElectron');
}

export function submitJobsToNative(jobs: NetworkPrinterJob[], usePrintQueue: boolean) {
  printToNativeOrQueue(makeSubmitJobMessage(jobs), usePrintQueue);
}

export function printTextToNative(job: NetworkPrinterJob) {
  printToNative(makePrintPlainTextMessage(job));
}

function makeCancelJobsMessage() {
  return {
    type: NetworkPrinterUtils.CANCEL_JOBS,
  };
}

export function cancelJobsToNative() {
  const message = makeCancelJobsMessage();
  const nativePort = getNativePort();

  if (!nativePort) {
    throw new Error('Could not communicate with native interface');
  }
  nativePort.postMessage(message);
}

interface Printers {
  networkPrinters: dashboard.BusinessPrinter[];
  savedPrinters: dashboard.BusinessPrinter[];
}

export function updateSavedPrinterIps(
  { networkPrinters, savedPrinters }: Printers,
  createOrUpdatePrinter: (printer: dashboard.BusinessPrinter) => Promise<any>,
  fetchAllSavedPrinters: () => void
) {
  let promise = Promise.resolve();
  let ipChanged = false;
  forEach(savedPrinters, (printer) => {
    const networkPrinter = find(networkPrinters, {
      macAddress: printer.macAddress,
    });
    if (networkPrinter && networkPrinter.ipAddress !== printer.ipAddress) {
      ipChanged = true;
      promise = promise.then(() => {
        return createOrUpdatePrinter({
          ...printer,
          ipAddress: networkPrinter.ipAddress,
        });
      });
    }
  });
  if (ipChanged) {
    promise = promise.then(fetchAllSavedPrinters);
  }
  return promise;
}

export function findByMacAddress(
  printers: dashboard.BusinessPrinter[],
  macAddress: string
): dashboard.BusinessPrinter | undefined {
  const address = lowerCase(macAddress);
  return find(printers, (printer) => lowerCase(printer.macAddress) === address);
}

export function isValidStarPrinterModel(modelName: string): boolean {
  return !startsWith(toLower(modelName), 'star');
}

export function isValidEpsonPrinterModel(modelName: string): boolean {
  const model = toUpper(modelName.replace(/[-_]/g, ''));
  return !!find(EpsonPrinterSeries, (name) => startsWith(model, name.replace('-', '')));
}

export function isEpsonPrinter(printer: dashboard.BusinessPrinter): boolean {
  return safeEnumCompare(
    printer.manufacturer,
    BusinessPrinter.Manufacturer,
    BusinessPrinter.Manufacturer.EPSON
  );
}

export function isWebPrinter(printer: dashboard.BusinessPrinter): boolean {
  return (
    safeEnumCompare(
      printer.communicationType,
      BusinessPrinter.CommunicationType,
      BusinessPrinter.CommunicationType.HTTP
    ) ||
    safeEnumCompare(
      printer.communicationType,
      BusinessPrinter.CommunicationType,
      BusinessPrinter.CommunicationType.HTTPS
    )
  );
}

export function supportsImagePrinting(printer: dashboard.BusinessPrinter): boolean {
  // Only the Epson TM-U220 and the StarMicronics SP700 line of printers will be issued
  // just text and not rasterized chits.
  return !(startsWith(printer.model, 'TM-U220') || startsWith(printer.model, 'SP7'));
}

export function canPrintTo(printer: dashboard.BusinessPrinter): boolean {
  return (
    (isWebPrinter(printer) && !getNativePort()) || (!isWebPrinter(printer) && !!getNativePort())
  );
}

export function canPrint(printers: dashboard.BusinessPrinter[]): boolean {
  return some(printers, canPrintTo);
}

interface RasterizeResult {
  renderResult: RenderResult;
  canvas: HTMLCanvasElement;
}

export enum PrinterPaperSize {
  THREE_INCHES,
  TWO_INCHES,
}

export async function rasterizeTextToImage(
  textToPrint: string,
  printerPaperSize = PrinterPaperSize.THREE_INCHES,
  heightPaddingFactor = 1
): Promise<RasterizeResult> {
  const printerWidth = printerPaperSize === PrinterPaperSize.THREE_INCHES ? 512 : 412;

  // First, create a hidden DOM element so that we can calculate an approximage height for the image
  const calcDiv = document.createElement('div');
  calcDiv.style.visibility = 'hidden';
  calcDiv.innerHTML = getSafeInnerHtml(printerWidth, textToPrint);
  document.body.appendChild(calcDiv);
  const height = calcDiv.clientHeight * heightPaddingFactor;
  document.body.removeChild(calcDiv);

  // Then, create a canvas that will contain the rasterized image.
  const canvas = document.createElement('canvas');
  canvas.width = printerWidth;
  canvas.height = height;

  // Finally, rasterize and adjust the resulting promise to create the printer request.
  const renderResult = await rasterizeHTML.drawHTML(calcDiv.innerHTML, canvas);
  return { renderResult, canvas };
}

export function getSafeInnerHtml(printerWidth: number, textToPrint: string) {
  const printStyles = `font-family: 'Arial'; font-size: 32px; max-width: ${printerWidth}px`;
  const sanitizedTextToPrint = DOMPurify.sanitize(textToPrint);
  return `<div style="${printStyles}">${sanitizedTextToPrint.replace(/\n/g, '<br/>')}</div>`;
}

export async function createBase64Image(
  textToPrint: string,
  printer: dashboard.BusinessPrinter
): Promise<string> {
  // We currently only support one 2" printer model, the TM-P60.
  const printerPaperSize = startsWith(printer.model, 'TM-P60')
    ? PrinterPaperSize.TWO_INCHES
    : PrinterPaperSize.THREE_INCHES;
  const result = await rasterizeTextToImage(textToPrint, printerPaperSize);
  return result.canvas.toDataURL().split(',')[1];
}

/**
 * Provides a naive estimation of how long it will take to print 1 chit on a printer.
 * A better estimation would take into consideration the amount of data being printed.
 */
export function estimatePrintDelay(printer: dashboard.BusinessPrinter): Duration {
  // This model is a dot matrix printer that is slower than most, this was a solution for
  // customer issue TK-4608.
  if (isEpsonPrinter(printer) && startsWith(printer.model, 'TM-U220')) {
    return Duration.ofSeconds(10 * printer.defaultNumCopiesToPrint);
  }
  return Duration.ofSeconds(DEFAULT_PRINTER_DELAY_SECONDS * printer.defaultNumCopiesToPrint);
}
